import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ServicesSection = () => {
  const services = [
    {
      icon: 'Shield',
      title: 'OSHA Compliance',
      description: 'Comprehensive workplace safety training covering OSHA standards, hazard prevention, and regulatory requirements.'
    },
    {
      icon: 'Users',
      title: 'HR Compliance',
      description: 'Master employment law, workplace policies, discrimination prevention, and HR best practices for legal compliance.'
    },
    {
      icon: 'Lock',
      title: 'Data Privacy & Security',
      description: 'Learn GDPR, CCPA, HIPAA regulations and implement robust data protection strategies for your organization.'
    },
    {
      icon: 'FileCheck',
      title: 'Regulatory Training',
      description: 'Stay current with evolving regulations across industries including finance, healthcare, and government sectors.'
    },
    {
      icon: 'Award',
      title: 'CPE Credit Courses',
      description: 'Earn continuing professional education credits through SHRM and HRCI approved compliance certification programs.'
    },
    {
      icon: 'TrendingUp',
      title: 'Risk Management',
      description: 'Develop effective compliance risk assessment frameworks and mitigation strategies for enterprise resilience.'
    }
  ];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={{
        hidden: { opacity: 0 },
        visible: { 
          opacity: 1,
          transition: { staggerChildren: 0.15 }
        }
      }}
      className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-green-50 via-emerald-50/20 to-green-50/30 relative overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-green-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-10 w-80 h-80 bg-emerald-100/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-green-200/10 rounded-full blur-2xl" />
        
        {/* Animated floating elements */}
        <motion.div 
          animate={{ 
            x: [0, 20, 0],
            y: [0, -20, 0]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-20 h-20 bg-gradient-to-r from-emerald-300/20 to-green-300/20 rounded-full"
        />
        <motion.div 
          animate={{ 
            x: [0, -20, 0],
            y: [0, 20, 0]
          }}
          transition={{ 
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-gradient-to-r from-green-300/20 to-emerald-300/20 rounded-full"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: { opacity: 1, y: 0 }
          }}
          className="text-center mb-16 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full border border-green-200 shadow-sm mb-8"
          >
            <motion.div
              animate={{ rotate: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Icon name="Briefcase" size={18} className="text-emerald-700" />
            </motion.div>
            <span className="font-semibold text-green-800 tracking-wide">Our Training Solutions</span>
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
          >
            <motion.span 
              className="bg-gradient-to-r from-emerald-700 via-green-600 to-emerald-700 bg-clip-text text-transparent"
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{ 
                duration: 5, 
                repeat: Infinity,
                ease: "linear" 
              }}
              style={{ backgroundSize: '200% 100%' }}
            >
              Comprehensive Compliance
            </motion.span>
            <br />
            <motion.span 
              className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 bg-clip-text text-transparent"
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{ 
                duration: 5, 
                repeat: Infinity,
                ease: "linear",
                delay: 0.5
              }}
              style={{ backgroundSize: '200% 100%' }}
            >
              Programs
            </motion.span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
          >
            Expert-led training solutions to keep your organization compliant, certified, and ahead of regulatory changes
          </motion.p>
        </motion.div>
        
        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={{
                hidden: { opacity: 0, y: 60, scale: 0.95 },
                visible: { 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  transition: { 
                    type: "spring", 
                    stiffness: 100,
                    damping: 15,
                    delay: index * 0.1 
                  }
                }
              }}
              whileHover={{ 
                y: -15,
                scale: 1.02,
                transition: { type: "spring", stiffness: 300 }
              }}
              className="group relative"
            >
              {/* Card Container */}
              <div className="relative bg-gradient-to-br from-white to-emerald-50 rounded-3xl border-2 border-green-100 hover:border-emerald-300/80 hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-500 overflow-hidden h-full">
                {/* Animated gradient border */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 via-emerald-500/0 to-green-600/0 group-hover:from-green-500/10 group-hover:via-emerald-500/10 group-hover:to-green-600/10 transition-all duration-500" />
                
                {/* Top accent line */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>

                {/* Content */}
                <div className="relative z-10 p-8 h-full flex flex-col">
                  {/* Icon Container */}
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-br from-green-500 via-emerald-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg self-start relative overflow-hidden"
                    whileHover={{ 
                      scale: 1.15,
                      rotate: 360,
                      transition: { duration: 0.5, type: "spring" }
                    }}
                    animate={{
                      boxShadow: [
                        '0 0 20px rgba(16, 185, 129, 0.3)',
                        '0 0 30px rgba(5, 150, 105, 0.5)',
                        '0 0 20px rgba(16, 185, 129, 0.3)'
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                    <Icon name={service.icon} size={28} className="text-white relative z-10" />
                  </motion.div>
                  
                  {/* Title */}
                  <motion.h3 
                    className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-700 transition-colors duration-300"
                    animate={{ 
                      textShadow: [
                        '0 0 0px rgba(5, 150, 105, 0)',
                        '0 0 10px rgba(5, 150, 105, 0.3)',
                        '0 0 0px rgba(5, 150, 105, 0)'
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    {service.title}
                  </motion.h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow text-base">
                    {service.description}
                  </p>

                  {/* Hover Action */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileHover={{ opacity: 1, x: 0 }}
                    className="flex items-center text-emerald-600 font-semibold pt-4 border-t border-green-100 group-hover:border-emerald-200 transition-colors duration-300"
                  >
                    <span className="text-sm tracking-wide">Explore Program</span>
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="ml-3"
                    >
                      <Icon name="ArrowRight" size={18} />
                    </motion.div>
                  </motion.div>
                </div>

                {/* Corner accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100/40 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-300 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              {/* Floating decorative element */}
              <div className="absolute -z-10 -inset-4 bg-gradient-to-br from-green-500/5 to-emerald-500/5 rounded-3xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-center mt-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1, type: "spring" }}
            className="inline-block p-8 rounded-3xl bg-gradient-to-br from-white to-green-50/50 border border-green-100 shadow-xl relative overflow-hidden"
          >
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-200/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-green-200/20 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Need a custom compliance solution?
              </h3>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto">
                Our experts can design tailored training programs to meet your specific regulatory requirements
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 text-white font-bold px-10 py-4 rounded-xl shadow-lg hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300 group/btn"
                >
                  <motion.div
                    animate={{ rotate: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Icon name="MessageCircle" size={22} />
                  </motion.div>
                  <span>Contact Our Experts</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Icon name="ArrowRight" size={22} />
                  </motion.div>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-3 border-2 border-green-300 text-green-700 font-bold px-10 py-4 rounded-xl hover:bg-green-50 transition-all duration-300"
                >
                  <motion.div
                    animate={{ rotate: [0, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  >
                    <Icon name="Calendar" size={22} />
                  </motion.div>
                  <span>Schedule Demo</span>
                </motion.button>
              </div>
              
              {/* Trust indicators */}
              <div className="mt-10 pt-8 border-t border-emerald-200">
                <p className="text-sm text-gray-500 mb-4">Trusted by industry leaders</p>
                <div className="flex flex-wrap justify-center items-center gap-6 lg:gap-8 opacity-80">
                  {['FORBES', 'SHRM', 'HRCI', 'OSHA'].map((company, index) => (
                    <motion.div
                      key={company}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.2 + index * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                      className="text-green-700 font-bold text-lg px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100"
                    >
                      {company}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.4 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12 pt-8 border-t border-emerald-100"
          >
            {[
              { value: '500+', label: 'Compliance Courses', icon: 'BookOpen' },
              { value: '10K+', label: 'Professionals Trained', icon: 'Users' },
              { value: '98%', label: 'Satisfaction Rate', icon: 'Star' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1.5 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="text-center p-6 bg-gradient-to-br from-white to-emerald-50 rounded-2xl border border-green-100 shadow-sm hover:shadow-md transition-all"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat.icon} size={24} className="text-green-600" />
                </div>
                <motion.div 
                  className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2"
                  animate={{ 
                    textShadow: [
                      '0 0 0px rgba(16, 185, 129, 0)',
                      '0 0 10px rgba(16, 185, 129, 0.3)',
                      '0 0 0px rgba(16, 185, 129, 0)'
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-gray-600 font-medium text-sm lg:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ServicesSection;